import { editTraversalOperations } from './editTraversalOperations';
import {
  MetadataComponent,
  TraversalBuilderState,
  TraversalBuilderViewState,
} from '../types';
import { componentTypeHierarchyOperations } from 'streams/componentTypeHierarchies/componentTypeHierarchyOperations';
import { viewpointBuilderFiltersOperations } from './viewpointBuilderFiltersOperations';
import { getViewpointBuilderSuggestionsLoaders } from '../addFiltersTab/getViewpointBuilderSuggestionsLoaders';
import { collapsePathsOperations } from '../collapsePathsTab/collapsePathsOperations';
import { requiredComponentsOperations } from '../requiredComponentsTab/requiredComponentsOperations';
import { isTruthy } from '@ardoq/common-helpers';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { Features } from '@ardoq/features';
import { MetamodelViewGraphEdge, MetaModelViewGraphNode } from '@ardoq/graph';
import {
  APIWorkspaceAttributes,
  ArdoqId,
  ArrowType,
  LineType,
  PARENT_REF_TYPE_NAME,
} from '@ardoq/api-types';
import { colors } from '@ardoq/design-tokens';
import { isHierarchyCollapsedByRules } from './pathCollapsing';

// A stable id so the edge is reused across renders instead of recreated.
const START_SET_EXPANSION_SELF_REFERENCE_ID =
  'start-set-expansion-self-reference';

// Solid black line, labelled as the parent-of reference - the unfolding follows
// the component hierarchy. Purely decorative: it is never part of the traversal
// definition. `ArrowType.BOTH` denotes both sides of a single arrowhead (the
// standard reference arrow), not both ends of the line; with `lineBeginning:
// NONE` this renders a single arrow at the end.
const startSetExpansionSelfReferenceStyle = {
  color: colors.black950,
  line: LineType.SOLID,
  lineEnding: ArrowType.BOTH,
  lineBeginning: ArrowType.NONE,
  displayLabel: PARENT_REF_TYPE_NAME,
};

/**
 * A recursive collapsing rule collapses the hierarchy along with its own hops,
 * so the self-reference belongs to the path that rule stands for:
 *
 * - `collapsed` - the rule's collapsed edge has replaced the path, so the
 *   self-reference goes with the rest of it.
 * - `inSelectedPath` - the rule is being selected, or its card is expanded and
 *   showing the full path, so the self-reference is highlighted with it.
 *
 * Several rules can collapse the hierarchy, so it only disappears once none of
 * them is showing its full path.
 */
const getSelfReferenceState = (
  state: TraversalBuilderState
): 'collapsed' | 'inSelectedPath' | 'plain' => {
  const { rules, currentEditableRule } = state.pathCollapsing;
  if (isHierarchyCollapsedByRules(rules)) {
    return 'collapsed';
  }
  const isShowingFullPath = rules.some(
    ({ isActive, isExpanded, collapseRecursively }) =>
      isActive && isExpanded && collapseRecursively
  );
  return isShowingFullPath || currentEditableRule?.collapseRecursively
    ? 'inSelectedPath'
    : 'plain';
};

/**
 * The rendered graph keeps the edge instance it was built with - the yFiles
 * builder matches by id and only refreshes labels - so a new instance would
 * carry its highlight nowhere. The traversal's own edges are mutated in place
 * for the same reason; this keeps one instance per start node to mutate too.
 * A rebuilt graph brings a new start node, and with it a new self-reference.
 */
const selfReferencesByStartNode = new WeakMap<
  MetaModelViewGraphNode,
  MetamodelViewGraphEdge
>();

const getSelfReference = (
  startNode: MetaModelViewGraphNode,
  graphNodes: Map<string, MetaModelViewGraphNode>
): MetamodelViewGraphEdge => {
  const existing = selfReferencesByStartNode.get(startNode);
  if (existing) {
    return existing;
  }
  const selfReference = MetamodelViewGraphEdge.create(
    START_SET_EXPANSION_SELF_REFERENCE_ID,
    startNode.id,
    startNode.id,
    graphNodes,
    START_SET_EXPANSION_SELF_REFERENCE_ID,
    // Fresh copy: the edge stores this object by reference and `setLabel`
    // mutates it in place, so a shared constant could leak across instances.
    { representationData: { ...startSetExpansionSelfReferenceStyle } }
  );
  selfReferencesByStartNode.set(startNode, selfReference);
  return selfReference;
};

// With start-set expansion the start node stands for the whole unfolded
// hierarchy (the start components plus all their descendants). Add a purely
// decorative self-reference on the start node so the type preview conveys that
// unfolding. This only affects the rendered builder graph, not the traversal
// definition or the collapsing logic.
export const withStartSetExpansionSelfReference = (
  state: TraversalBuilderState,
  graphEdges: Map<string, MetamodelViewGraphEdge>,
  graphNodes: Map<string, MetaModelViewGraphNode>
): Map<string, MetamodelViewGraphEdge> => {
  const { startNode, startSetExpansion } = state;
  if (!startSetExpansion || !startNode || !graphNodes.has(startNode.id)) {
    return graphEdges;
  }
  const selfReferenceState = getSelfReferenceState(state);
  if (selfReferenceState === 'collapsed') {
    return graphEdges;
  }
  const selfReference = getSelfReference(startNode, graphNodes);
  selfReference.setIsInSelectedPath(selfReferenceState === 'inSelectedPath');
  // The loop hangs off the start node, so it fades when the node does.
  selfReference.setOpacity(graphNodes.get(startNode.id)?.getOpacity() ?? 1);
  return new Map(graphEdges).set(
    START_SET_EXPANSION_SELF_REFERENCE_ID,
    selfReference
  );
};

/**
 * The types the hierarchy covers, as component metadata for the card in the
 * reference panel. The hierarchy itself comes from `componentTypeHierarchies$`,
 * so this is a lookup rather than a walk, while the representation data is read
 * from the raw metamodel nodes - a type the hierarchy contains need not be in
 * the traversal graph. The expansion swaps the start type for the wildcard
 * sentinel, so the type it started from is what the hierarchy is keyed on.
 */
const shouldShowStartSetExpansionToggle = (
  state: TraversalBuilderState
): boolean =>
  hasFeature(Features.START_SET_EXPANSION_WITH_DESCENDANTS) &&
  state.selectedGraphNodeId !== null &&
  state.selectedGraphNodeId === state.startNode?.id;

const getHierarchyTypeNames = (state: TraversalBuilderState): string[] => {
  const startType =
    state.startSetExpansionState?.originalStartComponentType ??
    state.startComponentType;
  if (!startType) {
    return [];
  }
  // While the expansion is in effect the hierarchy it stands for is already
  // recorded; deriving it a second time is how the two would drift apart.
  return (
    state.startSetExpansionState?.hierarchyComponentTypes ??
    componentTypeHierarchyOperations.getDescendantTypes(
      state.componentTypeHierarchies,
      startType
    )
  );
};

const getHierarchyComponentTypesToDisplay = (
  state: TraversalBuilderState
): MetadataComponent[] =>
  getHierarchyTypeNames(state)
    .map(typeName => state.rawGraphNodes.get(typeName)?.metaData)
    .filter(isTruthy);

/**
 * Hierarchy types the metamodel has no node for. Both lists come from the same
 * metamodel response, so a gap is an inconsistency rather than a case to
 * handle. Reported by the stream's `tap`, which owns the logging.
 */
export const getMissingHierarchyTypes = (
  state: TraversalBuilderState
): string[] => {
  if (!shouldShowStartSetExpansionToggle(state)) {
    return [];
  }
  return getHierarchyTypeNames(state).filter(
    typeName => !state.rawGraphNodes.get(typeName)?.metaData
  );
};

export const traversalEditStateToTraversalViewState = (
  state: TraversalBuilderState,
  workspacesById: Record<ArdoqId, APIWorkspaceAttributes>,
  organizationId: string = ''
): TraversalBuilderViewState => {
  const showStartSetExpansionToggle = shouldShowStartSetExpansionToggle(state);
  const relatedWorkspaceIds =
    viewpointBuilderFiltersOperations.getWorkspaceIdsForSelectedComponentType(
      state
    );
  const relatedWorkspaces = relatedWorkspaceIds
    .map(id => workspacesById[id])
    .filter(isTruthy);

  const asyncLoaders = getViewpointBuilderSuggestionsLoaders({
    organizationId,
    relatedWorkspaces,
    componentTypeName:
      editTraversalOperations.getSelectedNodeComponentType(state) ?? null,
  });

  const { collapsedGraphEdges, collapsedGraphNodes } =
    editTraversalOperations.buildCollapsedGraph(state);

  const graphEdges = withStartSetExpansionSelfReference(
    state,
    collapsedGraphEdges,
    collapsedGraphNodes
  );

  return {
    ...state,
    graphEdges,
    graphNodes: collapsedGraphNodes,
    triplesSortAndFiltersState: {
      ...state.triplesSortAndFiltersState,
      ...getOutgoingAndIncomingHiddenTriplesCount(state),
    },
    filtersDefinitions:
      viewpointBuilderFiltersOperations.getFiltersDefinitions(state),
    asyncLoaders,
    stateAsSavableAttributes:
      editTraversalOperations.getTraversalAsSavableAttributes(state),
    perspectivesOptionsArgs:
      editTraversalOperations.getWorkspaceIdsAndComponentAndReferenceTypeNamesFromTraversals(
        state
      ),
    pathCollapsingViewState:
      collapsePathsOperations.getPathCollapsingViewState(state),
    shouldShowDeleteCurrentNodeButton:
      state.selectedGraphNodeId !== null &&
      state.selectedGraphNodeId !== state.startNode?.id,
    shouldShowStartSetExpansionToggle: showStartSetExpansionToggle,
    // Only resolved when the card is on screen: the state emits on every
    // keystroke in the triples search.
    hierarchyComponentTypes: showStartSetExpansionToggle
      ? getHierarchyComponentTypesToDisplay(state)
      : [],
    requiredComponentsState:
      requiredComponentsOperations.getRequiredComponentsViewState(state),
    shouldIncludeClickOtherComponentTypeNodesNotification:
      state.graphNodes.size > 1 &&
      state.shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings,
  };
};

const getOutgoingAndIncomingHiddenTriplesCount = (
  state: TraversalBuilderState
) => {
  if (!state.triplesSortAndFiltersState.showOnlyOptionsWithInstanceCounts) {
    return {
      incomingTriplesHiddenCount: 0,
      outgoingTriplesHiddenCount: 0,
    };
  }

  const allMatchingFilteredOptionsIncomingCount =
    editTraversalOperations.filterOptions(
      state.tripleOptions.incoming,
      state.triplesSortAndFiltersState.filterTerm,
      false
    ).length;
  const allMatchingFilteredOptionsOutgoingCount =
    editTraversalOperations.filterOptions(
      state.tripleOptions.outgoing,
      state.triplesSortAndFiltersState.filterTerm,
      false
    ).length;

  const outgoingTriplesHiddenCount =
    allMatchingFilteredOptionsOutgoingCount -
    state.filteredTripleOptions.outgoing.length;

  const incomingTriplesHiddenCount =
    allMatchingFilteredOptionsIncomingCount -
    state.filteredTripleOptions.incoming.length;

  return {
    incomingTriplesHiddenCount,
    outgoingTriplesHiddenCount,
  };
};
